// ===== Runtime probe =====
// Injected as the first script in the build's <head>, so it is in place before
// the creative's own code runs — errors and the first audio call are only
// observable from there.
//
// Everything it learns is reported to the parent by postMessage. It never
// blocks or changes what the build does: hooks call through to the original.

// An MRAID v2.0 container stub. Networks that run on MRAID (AppLovin, and the
// SDK-injected containers generally) never ship the object themselves — the ad
// SDK puts it on `window` before the creative runs. In a plain browser it is
// simply absent, so the creative either dies on load or falls into its no-SDK
// fallback, and nothing about the real integration gets exercised.
//
// Shaped from the IAB MRAID spec: starts in "loading", flips to "default" and
// fires `ready` on the next tick, then reports itself viewable. open() and
// close() are recorded and deliberately inert — a real open() would navigate
// the frame away to the store.
//
// The version is per-network: AppLovin asks for MRAID 2.0, Unity Ads for 3.0,
// and a creative that gates on getVersion() must see the one its container
// would actually report.
const mraidStub = (version) => `
  (function () {
    var listeners = {};
    var state = 'loading';
    var viewable = true;
    function emit(ev) {
      (listeners[ev] || []).slice().forEach(function (fn) {
        try { fn.apply(null, [].slice.call(arguments, 1)); } catch (e) {}
      });
    }
    // First time the creative reads the container, not merely that we installed
    // it — otherwise "MRAID 연동" would pass for a build that ignores MRAID.
    var used = false;
    function noteUse() { if (!used) { used = true; send('call', { id: 'mraid-use' }); } }

    var mraid = {
      getVersion: function () { noteUse(); return '${version}'; },
      getState: function () { noteUse(); return state; },
      getPlacementType: function () { return 'interstitial'; },
      isViewable: function () { noteUse(); return viewable; },
      addEventListener: function (ev, fn) { noteUse(); (listeners[ev] = listeners[ev] || []).push(fn); },
      removeEventListener: function (ev, fn) {
        if (!listeners[ev]) return;
        if (!fn) { delete listeners[ev]; return; }
        listeners[ev] = listeners[ev].filter(function (f) { return f !== fn; });
      },
      open: function (url) { send('call', { id: 'cta', gestures: gestures, args: [String(url)] }); },
      close: function () { state = 'hidden'; send('call', { id: 'close', args: [] }); emit('stateChange', state); },
      useCustomClose: function (v) { send('call', { id: 'useCustomClose', args: [String(v)] }); },
      expand: function () {}, resize: function () {},
      supports: function (f) { return f === 'orientation'; },
      setOrientationProperties: function () {}, getOrientationProperties: function () { return {}; },
      setExpandProperties: function () {}, getExpandProperties: function () { return {}; },
      setResizeProperties: function () {}, getResizeProperties: function () { return {}; },
      getScreenSize: function () { return { width: innerWidth, height: innerHeight }; },
      getMaxSize: function () { return { width: innerWidth, height: innerHeight }; },
      getDefaultPosition: function () { return { x: 0, y: 0, width: innerWidth, height: innerHeight }; },
      getCurrentPosition: function () { return { x: 0, y: 0, width: innerWidth, height: innerHeight }; },
      playVideo: function () {}, storePicture: function () {}, createCalendarEvent: function () {},
      // Container extension, not core MRAID: the Luna builds read it for
      // impression/creative ids. Absent, their call throws into a catch and the
      // ad-data path is silently skipped.
      getMraidAdData: function () {
        noteUse();
        return { UII: 'paw-preview-impression', creativeId: 'paw-preview', campaignId: 'paw-preview' };
      },
    };
    window.mraid = mraid;
    // Next tick, not synchronously: a creative that registers its ready handler
    // after this script would miss an immediate event.
    setTimeout(function () {
      state = 'default';
      send('ready', { id: 'mraid' });
      emit('ready');
      // Unity Ads gates playable start on viewableChange rather than ready, so
      // the stub has to actually deliver it.
      emit('viewableChange', viewable);
      emit('stateChange', state);
    }, 0);
  }());
`;

// A Mintegral host stub. Mintegral does not use MRAID at all — the creative and
// the player split a small set of globals between them:
//
//   creative defines  gameStart, gameClose
//   creative calls    gameReady, gameEnd, install
//   host defines      gameReady, gameEnd, install, MW_INIT
//   host calls        gameStart, gameClose
//
// The half the player owns is simply missing in a browser, and one consequence
// is not subtle: the build pauses itself on luna:build and only resumes when the
// host calls gameStart() after the creative reports ready. Without this stub the
// preview shows a frozen first frame and nothing about the integration runs.
const MINTEGRAL_STUB = `
  (function () {
    window.MW_INIT = { isRewardAds: function () { return false; } };

    window.gameEnd = function () { send('call', { id: 'gameend' }); };
    window.install = function () { send('call', { id: 'cta', gestures: gestures }); };

    window.gameReady = function () {
      send('call', { id: 'gameready' });
      // What the player does next: acknowledge ready, then start the game.
      setTimeout(function () {
        if (typeof window.gameStart === 'function') {
          send('call', { id: 'gamestart' });
          window.gameStart();
        }
      }, 0);
    };
  }());
`;

// A Meta host stub. Meta injects FbPlayableAd; the creative only calls it, and
// its CTA is the single call FbPlayableAd.onCTAClick(). Meta explicitly does
// not use MRAID, so nothing else is provided here — an mraid stub would let a
// creative take a path Meta would reject.
//
// The logging entry points come from the Luna runtime, which calls them behind
// a `typeof FbPlayableAd` guard: without them the creative silently takes its
// no-SDK branch and none of the reporting is exercised.
const META_STUB = `
  (function () {
    var fb = window.FbPlayableAd || (window.FbPlayableAd = {});
    fb.onCTAClick = function () { send('call', { id: 'cta', gestures: gestures }); };
    fb.logGameLoad = function () { send('call', { id: 'fb-gameload' }); };
    fb.logLevelComplete = function (n) { send('call', { id: 'fb-level', args: [String(n)] }); };
    fb.logButtonClick = function (n) { send('call', { id: 'fb-button', args: [String(n)] }); };
    fb.initializeLogging = function () { send('call', { id: 'fb-init' }); };
  }());
`;

// A Smadex host stub. The creative reports through window.smxTracking, which
// the Smadex container supplies — the bridge guards every call with
// `typeof smxTracking !== "undefined"`, so without this the reporting is
// silently skipped and only the store fallback is exercised.
//
// Timestamps ride along on download/complete: the spec says the two must never
// trigger together, which can only be judged from when each actually fired.
const SMADEX_STUB = `
  (function () {
    window.smxTracking = {
      load:     function () { send('call', { id: 'smx-load', t: Date.now() }); },
      download: function () { send('call', { id: 'smx-download', t: Date.now(), gestures: gestures }); },
      complete: function () { send('call', { id: 'smx-complete', t: Date.now() }); },
      redirect: function () { send('call', { id: 'cta', t: Date.now(), gestures: gestures }); },
    };
  }());
`;

// `hooks` describe the SDK entry points to watch, e.g.
//   [{ id: 'cta', global: 'ExitApi', method: 'exit' }]
// `provide` names container stubs to install before the creative runs.
export function probeSource(hooks, provide = [], opts = {}) {
  return `<script>(function(){
  var HOOKS = ${JSON.stringify(hooks)};
  function send(type, data) {
    try { parent.postMessage({ __pawVerify: 1, type: type, data: data || null }, '*'); } catch (e) {}
  }

  // First real user input. The "no sound before interaction" rule is about
  // ordering, so audio calls are judged against this flag, not against a clock.
  // isTrusted is the whole point: a Luna build synthesizes its own pointer
  // events, and counting those as input would mark "silent until interaction"
  // as passed before anyone has touched the ad.
  var gesture = false;
  var gestures = 0;
  // Counted, not just flagged: "must not redirect to app stores on the first
  // tap" can only be judged against how many inputs preceded the CTA.
  ['pointerdown', 'touchstart', 'mousedown', 'keydown'].forEach(function (ev) {
    addEventListener(ev, function (e) {
      if (!e.isTrusted) return;
      gesture = true;
      gestures++;
      send('gesture', { n: gestures });
    }, true);
  });

  addEventListener('error', function (e) {
    send('error', { message: String(e.message || (e.error && e.error.message) || e.type),
                    where: (e.filename || '') + (e.lineno ? ':' + e.lineno : '') });
  }, true);
  // A blocked-autoplay rejection is the browser enforcing its audio policy, not
  // a defect — it is in fact evidence the build honours "muted until
  // interaction". Report it as audio evidence so it can't fail the error check.
  var AUTOPLAY_RE = /NotAllowedError|autoplay|user (?:interaction|gesture|activation)|play\(\) (?:failed|request)/i;
  addEventListener('unhandledrejection', function (e) {
    var r = e.reason;
    var msg = String((r && r.name === 'NotAllowedError' ? r.name + ': ' : '') + ((r && r.message) || r));
    if (AUTOPLAY_RE.test(msg)) send('autoplay-blocked', { message: msg });
    else send('error', { message: 'unhandled rejection: ' + msg, where: '' });
  });

  // Audio. Report what actually started, not what was attempted: before a user
  // gesture the browser refuses both of these — play() rejects and resume()
  // never settles, leaving the context suspended — so counting the call itself
  // marks a build as noisy for sound that was never audible. A muted element or
  // a zero volume isn't "sound" either, so the state rides along and the parent
  // decides.
  var play = HTMLMediaElement.prototype.play;
  HTMLMediaElement.prototype.play = function () {
    var el = this;
    var p = play.apply(this, arguments);
    Promise.resolve(p).then(function () {
      send('audio', { gesture: gesture, muted: !!el.muted, volume: el.volume, kind: el.tagName });
    }, function () { send('autoplay-blocked', { message: 'play() rejected' }); });
    return p;
  };
  var AC = window.AudioContext || window.webkitAudioContext;
  if (AC && AC.prototype.resume) {
    var resume = AC.prototype.resume;
    AC.prototype.resume = function () {
      var ctx = this;
      var p = resume.apply(this, arguments);
      Promise.resolve(p).then(function () {
        // Reaching 'running' is the only way this became audible.
        send('audio', { gesture: gesture, muted: ctx.state !== 'running', volume: 1, kind: 'AudioContext' });
      }, function () {});
      return p;
    };
  }

  // Outbound network calls. blob:/data: are the preview's own inlined assets,
  // not remote fetches, so only absolute http(s) targets are reported.
  function noteNet(kind, url) {
    var u = String(url || '');
    if (/^(?:blob:|data:)/i.test(u)) return;
    // Resolve protocol-relative and root-relative forms before judging.
    var abs;
    try { abs = new URL(u, location.href).href; } catch (e) { abs = u; }
    if (/^https?:/i.test(abs)) send('net', { kind: kind, url: abs.slice(0, 300) });
  }
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input) {
      noteNet('fetch', typeof input === 'string' ? input : (input && input.url));
      return origFetch.apply(this, arguments);
    };
  }
  var xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    noteNet('xhr', url);
    return xhrOpen.apply(this, arguments);
  };
  if (navigator.sendBeacon) {
    var beacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = function (url) { noteNet('beacon', url); return beacon.apply(null, arguments); };
  }

  // SDK entry points. The build may define these long after load — a Luna
  // export registers ExitApi from its own inlined bridge — so poll instead of
  // reading once, and re-wrap if the build replaces the function later.
  var wrapped = {};
  setInterval(function () {
    HOOKS.forEach(function (h) {
      var obj = window[h.global];
      if (!obj || typeof obj[h.method] !== 'function') return;
      if (wrapped[h.id] === obj[h.method]) return;
      var orig = obj[h.method];
      var fn = function () {
        send('call', { id: h.id, gestures: gestures, args: [].slice.call(arguments, 0, 3).map(String) });
        return orig.apply(this, arguments);
      };
      obj[h.method] = fn;
      wrapped[h.id] = fn;
      send('ready', { id: h.id });
    });
  }, 200);

  ${provide.includes('mraid') ? mraidStub(opts.mraidVersion || '3.0') : ''}
  ${provide.includes('mintegral') ? MINTEGRAL_STUB : ''}
  ${provide.includes('meta') ? META_STUB : ''}
  ${provide.includes('smadex') ? SMADEX_STUB : ''}

  send('boot');
}());</scr` + `ipt>`;
}

// Put the probe first inside <head> so it precedes the creative's scripts.
// Falls back through <html> to the document start for a fragment with no head.
export function injectProbe(html, hooks, provide, opts) {
  const src = probeSource(hooks, provide, opts);
  const head = /<head[^>]*>/i.exec(html);
  if (head) return html.slice(0, head.index + head[0].length) + src + html.slice(head.index + head[0].length);
  const htmlTag = /<html[^>]*>/i.exec(html);
  if (htmlTag) return html.slice(0, htmlTag.index + htmlTag[0].length) + src + html.slice(htmlTag.index + htmlTag[0].length);
  return src + html;
}
